// SPDX-License-Identifier: BUSL-1.1

/**
 * Fleet-wide activity SSE consumer (CEO-V4 Phase 2 / L2).
 *
 * Mirrors `useSessionStream` (fetch-based SSE with stored API token —
 * EventSource cannot send custom Authorization headers) but points at the
 * org-scoped fleet stream:
 *
 *   GET /api/orgs/:orgId/activity/stream
 *
 * Returns a rolling buffer of recent activity events. Consumer components
 * (e.g. <LiveActivityCell>) filter the buffer in-memory by `agentId`, so
 * the operators grid mounts ONE stream per org, not one per row.
 *
 * Reconnect strategy: exponential backoff, identical cadence to
 * `useSessionStream` (1s / 2s / 5s / 10s). The connection re-mounts
 * automatically when the user switches orgs via OrgContext.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getStoredToken } from '@/lib/auth';

export interface FleetActivityEvent {
  /** ops_agent_activity.id — used as React key + dedupe handle. */
  id: string;
  agentId: string;
  sessionId: string | null;
  kind: string;
  occurredAt: string;
  payload: unknown;
  /** Client-side receive timestamp — useful for relative-time rendering. */
  receivedAt: number;
}

interface FleetActivityStreamState {
  connected: boolean;
  events: FleetActivityEvent[];
  error: string | null;
}

/** Max events retained in the rolling buffer. */
const MAX_EVENTS = 100;

/** Exponential backoff delays (ms) for reconnect attempts. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000];

/**
 * Subscribe to the org-wide live activity stream.
 *
 * Pass `orgId = null` to disconnect (e.g. when no org is selected yet).
 * Switching `orgId` aborts the previous connection and opens a fresh one.
 */
export function useFleetActivityStream(
  orgId: string | null,
  apiUrl: string,
) {
  const [state, setState] = useState<FleetActivityStreamState>({
    connected: false,
    events: [],
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  const connect = useCallback(() => {
    if (!orgId) return;

    const token = getStoredToken();
    const controller = new AbortController();
    abortRef.current = controller;

    // Reset buffer when (re)connecting — keeps the state honest about which
    // org the events came from.
    setState({ connected: false, events: [], error: null });

    const url = `${apiUrl}/orgs/${orgId}/activity/stream`;

    const run = async (attempt = 0) => {
      try {
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          const text = await res.text().catch(() => '');
          setState((s) => ({
            ...s,
            connected: false,
            error: `HTTP ${res.status}: ${text}`,
          }));
          return;
        }

        setState((s) => ({ ...s, connected: true, error: null }));

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const raw of frames) {
            if (!raw.trim() || raw.trim().startsWith(':')) continue;

            let eventName = 'message';
            const dataLines: string[] = [];
            for (const line of raw.split('\n')) {
              if (line.startsWith('event:'))
                eventName = line.slice(6).trim();
              else if (line.startsWith('data:'))
                dataLines.push(line.slice(5).trim());
            }
            const data = dataLines.join('\n');

            if (eventName === 'error') {
              try {
                const parsed = JSON.parse(data);
                setState((s) => ({ ...s, error: parsed.message ?? 'stream error' }));
              } catch {
                /* malformed error frame — ignore */
              }
              continue;
            }

            // Default frames are activity-event JSON.
            try {
              const parsed = JSON.parse(data) as Omit<
                FleetActivityEvent,
                'receivedAt'
              >;
              const event: FleetActivityEvent = {
                ...parsed,
                receivedAt: Date.now(),
              };
              setState((s) => ({
                ...s,
                events: [...s.events.slice(-(MAX_EVENTS - 1)), event],
              }));
            } catch {
              /* malformed event payload — skip */
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        setState((s) => ({
          ...s,
          connected: false,
          error: `Connection lost — retrying in ${delay / 1_000}s`,
        }));
        setTimeout(() => {
          if (!controller.signal.aborted) run(attempt + 1);
        }, delay);
        return;
      } finally {
        setState((s) => ({ ...s, connected: false }));
      }
    };

    run();
  }, [orgId, apiUrl]);

  useEffect(() => {
    connect();
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [connect]);

  const disconnect = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({ ...s, connected: false }));
  }, []);

  return { ...state, disconnect };
}
